// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: merging whiteboard scenes. A board is a list of Excalidraw elements; two people
// saving at once never conflict, because each element carries a version. Per element id
// the copy with the higher version wins, and on a tie the lower versionNonce wins (the
// rule Excalidraw's own collaboration uses, so every side picks the same copy). Deleted
// elements stay in the list with isDeleted: true so a delete reaches everyone.

export const BOARD_ID_PATTERN = /^[a-z0-9]{6,32}$/;
export const MAX_BOARD_ELEMENTS = 5000;
export const MAX_BOARD_SCENE_BYTES = 3_000_000;
export const MAX_BOARD_FILE_BYTES = 8 * 1024 * 1024;
export const BOARD_IMAGE_TYPES: ReadonlyArray<string> = [
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
	'image/svg+xml',
];

export interface BoardElementLike {
	id: string;
	version: number;
	versionNonce: number;
	isDeleted?: boolean;
	/** Excalidraw's fractional z-order key. */
	index?: string | null;
	type?: string;
	fileId?: string | null;
	[key: string]: unknown;
}

export interface BoardFileMeta {
	id: string;
	mime_type: string;
	size: number;
	created: number;
}

export interface BoardMergeResult<T extends BoardElementLike> {
	elements: Array<T>;
	/** True when `incoming` added or replaced at least one element of `stored`. */
	changed: boolean;
}

/** True when `candidate` should replace `current` (same id). */
export function boardElementWins(candidate: BoardElementLike, current: BoardElementLike): boolean {
	if (candidate.version !== current.version) return candidate.version > current.version;
	return candidate.versionNonce < current.versionNonce;
}

function isElement(value: unknown): value is BoardElementLike {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
	const el = value as Record<string, unknown>;
	return (
		typeof el.id === 'string' &&
		el.id.length > 0 &&
		el.id.length <= 64 &&
		typeof el.version === 'number' &&
		Number.isFinite(el.version) &&
		typeof el.versionNonce === 'number' &&
		Number.isFinite(el.versionNonce)
	);
}

/** Keeps only well-formed elements; returns null when `value` is not a list at all. */
export function sanitizeBoardElements(value: unknown): Array<BoardElementLike> | null {
	if (!Array.isArray(value)) return null;
	const seen = new Set<string>();
	const out: Array<BoardElementLike> = [];
	for (const item of value) {
		if (!isElement(item) || seen.has(item.id)) continue;
		seen.add(item.id);
		out.push(item);
		if (out.length >= MAX_BOARD_ELEMENTS) break;
	}
	return out;
}

/** Keeps only file entries shaped like {id, mime_type, size, created} with an allowed image type. */
export function sanitizeBoardFiles(value: unknown): Record<string, BoardFileMeta> {
	const out: Record<string, BoardFileMeta> = {};
	if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
	for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
		if (!raw || typeof raw !== 'object') continue;
		const file = raw as Record<string, unknown>;
		if (typeof file.id !== 'string' || file.id !== key || !/^[A-Za-z0-9_-]{1,128}$/.test(file.id)) continue;
		if (typeof file.mime_type !== 'string' || !BOARD_IMAGE_TYPES.includes(file.mime_type)) continue;
		out[key] = {
			id: file.id,
			mime_type: file.mime_type,
			size: typeof file.size === 'number' && file.size >= 0 ? file.size : 0,
			created: typeof file.created === 'number' ? file.created : 0,
		};
	}
	return out;
}

function compareIndex(a: BoardElementLike, b: BoardElementLike): number {
	const ai = typeof a.index === 'string' ? a.index : null;
	const bi = typeof b.index === 'string' ? b.index : null;
	if (ai === null || bi === null || ai === bi) return 0;
	// Fractional indices sort by plain code-unit order (base62: 0-9 < A-Z < a-z).
	return ai < bi ? -1 : 1;
}

/**
 * Folds `incoming` into `stored`. Elements only one side has are kept; elements both have
 * resolve by version. The result is in z-order when elements carry fractional indices,
 * otherwise stored order with new elements appended in the order they came.
 */
export function mergeBoardElements<T extends BoardElementLike>(
	stored: ReadonlyArray<T>,
	incoming: ReadonlyArray<T>,
): BoardMergeResult<T> {
	const byId = new Map<string, T>();
	const order: Array<string> = [];
	for (const el of stored) {
		if (byId.has(el.id)) continue;
		byId.set(el.id, el);
		order.push(el.id);
	}
	let changed = false;
	for (const el of incoming) {
		const current = byId.get(el.id);
		if (!current) {
			byId.set(el.id, el);
			order.push(el.id);
			changed = true;
		} else if (boardElementWins(el, current)) {
			byId.set(el.id, el);
			changed = true;
		}
	}
	const elements = order.map((id) => byId.get(id) as T);
	// Array.prototype.sort is stable, so elements without an index keep their place relative to each other.
	elements.sort(compareIndex);
	return {elements, changed};
}

/** File ids that live (not deleted) image elements point at. */
export function referencedBoardFileIds(elements: ReadonlyArray<BoardElementLike>): Set<string> {
	const ids = new Set<string>();
	for (const el of elements) {
		if (el.isDeleted || el.type !== 'image') continue;
		if (typeof el.fileId === 'string' && el.fileId) ids.add(el.fileId);
	}
	return ids;
}
